import { useState, useEffect } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@munawb/supabase';
import type { QualityRcaRow, IncidentRow, IncidentLogRow } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatDate, truncate } from '../lib/utils';

/* ─── Types ──────────────────────────────────────────────────────────────── */

type TabId = 'overview' | 'rca' | 'all-events';

type RcaWithIncident = QualityRcaRow & {
  incidents: Pick<IncidentRow, 'event_id' | 'event_type' | 'risk_level' | 'location' | 'description'> | null;
};

type IncidentWithDept = IncidentRow & { departments?: { name: string } | null };

/* ─── Toast ──────────────────────────────────────────────────────────────── */

type ToastItem = { id: number; msg: string; type: 'success' | 'error' | 'info' };
let _toastId = 0;
function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const push = (msg: string, type: ToastItem['type'] = 'info') => {
    const id = ++_toastId;
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };
  return { toasts, push };
}

/* ═══════════════════════════════════════════════════════════════════════════
   QualityPage
═══════════════════════════════════════════════════════════════════════════ */

export default function QualityPage() {
  const { profile }        = useAuthStore();
  const qc                 = useQueryClient();
  const { toasts, push }   = useToast();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [darkMode, setDarkMode]   = useState(() => document.documentElement.classList.contains('dark'));

  /* ─── Dark mode ── */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('munawb-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  /* ─── Search / Filter state ────────────────────────────────────────────── */
  const [allEventsSearch, setAllEventsSearch]   = useState('');
  const [allEventsStatus, setAllEventsStatus]   = useState('');
  const [rcaSearch, setRcaSearch]               = useState('');
  const [rcaStatusFilter, setRcaStatusFilter]   = useState('');

  /* ─── Modal state ───────────────────────────────────────────────────────── */
  const [selectedIncident, setSelectedIncident] = useState<IncidentWithDept | null>(null);
  const [selectedRca, setSelectedRca]           = useState<RcaWithIncident | null>(null);
  const [showRcaForm, setShowRcaForm]           = useState<string | null>(null); // incidentId
  const [showLogout, setShowLogout]             = useState(false);
  const [showProfile, setShowProfile]           = useState(false);

  /* ─── Notifications ──────────────────────────────────────────────────────── */
  const [showNotifs, setShowNotifs] = useState(false);

  /* ─── Queries ──────────────────────────────────────────────────────────── */

  // All incidents (for all-events tab + overview)
  const { data: allIncidents = [], isLoading: allLoading } = useQuery<IncidentWithDept[]>({
    queryKey: ['quality-all-incidents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('incidents')
        .select('*, departments(name)')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as IncidentWithDept[];
    },
  });

  // All RCAs
  const { data: rcaList = [], isLoading: rcaLoading } = useQuery<RcaWithIncident[]>({
    queryKey: ['quality-rca-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quality_rca')
        .select('*, incidents(event_id, event_type, risk_level, location, description)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RcaWithIncident[];
    },
  });

  // Notifications
  const { data: notifications = [] } = useQuery({
    queryKey: ['quality-notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`target_user.eq.${profile?.id},target_dept.eq.${profile?.dept_id}`)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) return [];
      return data ?? [];
    },
    enabled: !!profile,
  });

  // Event categories for quality override
  const { data: categories = [] } = useQuery<{ id: string; main_cat: string; sub_cat: string | null }[]>({
    queryKey: ['event-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_categories')
        .select('id, main_cat, sub_cat')
        .eq('active', true)
        .order('main_cat');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedIncident,
  });

  // Incident logs for detail modal
  const { data: incidentLogs = [] } = useQuery<IncidentLogRow[]>({
    queryKey: ['incident-logs', selectedIncident?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('incident_logs')
        .select('*')
        .eq('incident_id', selectedIncident!.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!selectedIncident,
  });

  /* ─── Derived overview KPIs ─────────────────────────────────────────────── */
  const totalIncidents  = allIncidents.length;
  const rcaPending      = allIncidents.filter(i => i.status === 'طلب RCA').length;
  const repeatedCount   = allIncidents.filter(i => i.is_repeated).length;
  const highRiskCount   = allIncidents.filter(i => i.risk_level === 'حرج' || i.risk_level === 'عالي' || i.risk_level === 'مرتفع').length;

  // Chart: status distribution
  const statusGroups: Record<string, number> = {};
  allIncidents.forEach(i => { statusGroups[i.status] = (statusGroups[i.status] ?? 0) + 1; });
  const statusEntries = Object.entries(statusGroups).sort((a, b) => b[1] - a[1]);

  // Chart: risk distribution
  const riskGroups: Record<string, number> = {};
  allIncidents.forEach(i => { riskGroups[i.risk_level] = (riskGroups[i.risk_level] ?? 0) + 1; });
  const riskEntries = Object.entries(riskGroups).sort((a, b) => b[1] - a[1]);

  // Chart: repeated vs normal
  const repeatedEntries = [
    ['متكرر', repeatedCount],
    ['عادي', totalIncidents - repeatedCount],
  ] as [string, number][];

  // Chart: by dept (top 6)
  const deptGroups: Record<string, number> = {};
  allIncidents.forEach(i => {
    const d = (i as any).departments?.name ?? i.location ?? 'غير محدد';
    deptGroups[d] = (deptGroups[d] ?? 0) + 1;
  });
  const deptEntries = Object.entries(deptGroups).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const statusColors: Record<string, string> = {
    'مفتوح': '#3b82f6', 'مصعد': '#ef4444', 'موجه': '#8b5cf6',
    'طلب RCA': '#f59e0b', 'مغلق': '#10b981', 'RCA Completed': '#06b6d4',
  };

  /* ─── Filtered events ──────────────────────────────────────────────────── */
  const filteredAllEvents = allIncidents.filter(i => {
    if (allEventsStatus && i.status !== allEventsStatus) return false;
    if (!allEventsSearch) return true;
    const s = allEventsSearch.toLowerCase();
    return (
      i.event_id.toLowerCase().includes(s) ||
      i.event_type.toLowerCase().includes(s) ||
      i.location.toLowerCase().includes(s) ||
      i.reporter_name.toLowerCase().includes(s) ||
      i.description.toLowerCase().includes(s)
    );
  });

  // RCA incidents (طلب RCA + RCA Completed)
  const rcaIncidents = allIncidents.filter(i => i.status === 'طلب RCA' || i.status === 'RCA Completed');
  const filteredRcaIncidents = rcaIncidents.filter(i => {
    if (rcaStatusFilter && i.status !== rcaStatusFilter) return false;
    if (!rcaSearch) return true;
    const s = rcaSearch.toLowerCase();
    return (
      i.event_id.toLowerCase().includes(s) ||
      i.event_type.toLowerCase().includes(s) ||
      i.location.toLowerCase().includes(s) ||
      i.reporter_name.toLowerCase().includes(s)
    );
  });

  /* ─── Mutations ─────────────────────────────────────────────────────────── */

  // Save/Update RCA
  const { mutateAsync: saveRca, isPending: rcaSaving } = useMutation({
    mutationFn: async (payload: Partial<QualityRcaRow> & { incident_id: string }) => {
      if (payload.id) {
        const { error } = await supabase.from('quality_rca').update(payload).eq('id', payload.id);
        if (error) throw error;
      } else {
        const rcaId = `RCA-${payload.incident_id.slice(0, 8).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
        const { error } = await supabase
          .from('quality_rca')
          .insert({ ...payload, rca_id: rcaId, analyst_id: profile?.id });
        if (error) throw error;
        // Update incident status
        await supabase.from('incidents').update({ status: 'RCA Completed' }).eq('id', payload.incident_id);
        await supabase.from('incident_logs').insert({
          incident_id: payload.incident_id,
          actor_id: profile?.id,
          actor_name: profile?.name ?? 'مشرف الجودة',
          action_type: 'rca_completed',
          action_name: 'اكتمل تحليل RCA',
          description: 'تم توثيق التحليل الجذري وإغلاق طلب RCA',
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quality-rca-list'] });
      qc.invalidateQueries({ queryKey: ['quality-all-incidents'] });
      setShowRcaForm(null);
      setSelectedRca(null);
      push('تم حفظ تحليل RCA بنجاح', 'success');
    },
    onError: (e: any) => push(e.message, 'error'),
  });

  // Quality Override
  const [overrideForm, setOverrideForm] = useState({ type: '', risk: '', justification: '' });
  const [overrideSaving, setOverrideSaving] = useState(false);

  useEffect(() => {
    if (selectedIncident) {
      setOverrideForm({ type: selectedIncident.event_type, risk: selectedIncident.risk_level, justification: '' });
    }
  }, [selectedIncident]);

  const submitQualityOverride = async () => {
    if (!selectedIncident) return;
    if (!overrideForm.justification.trim()) {
      push('مبرر التدخل إلزامي', 'error');
      return;
    }
    setOverrideSaving(true);
    try {
      const updates: Record<string, string> = {};
      if (overrideForm.type !== selectedIncident.event_type) updates.event_type = overrideForm.type;
      if (overrideForm.risk !== selectedIncident.risk_level) updates.risk_level  = overrideForm.risk;

      if (Object.keys(updates).length > 0) {
        const { error } = await supabase.from('incidents').update(updates).eq('id', selectedIncident.id);
        if (error) throw error;
      }

      await supabase.from('incident_logs').insert({
        incident_id: selectedIncident.id,
        actor_id:    profile?.id,
        actor_name:  profile?.name ?? 'مشرف الجودة',
        action_type: 'quality_override',
        action_name: 'تقييم الجودة',
        description: overrideForm.justification,
      });

      qc.invalidateQueries({ queryKey: ['quality-all-incidents'] });
      qc.invalidateQueries({ queryKey: ['incident-logs', selectedIncident.id] });
      push('تم اعتماد تقييم الجودة وتوثيقه', 'success');
      setSelectedIncident(null);
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Mark notifications as read
  const markAllRead = async () => {
    await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .or(`target_user.eq.${profile?.id},target_dept.eq.${profile?.dept_id}`)
      .eq('is_read', false);
    qc.invalidateQueries({ queryKey: ['quality-notifications'] });
  };

  const handleSignOut = async () => { setShowLogout(false); await supabase.auth.signOut(); };

  /* ─── Request RCA mutation ──────────────────────────────────────────────── */
  const { mutateAsync: requestRca, isPending: requestingRca } = useMutation({
    mutationFn: async (incidentId: string) => {
      const { error } = await supabase.from('incidents').update({ status: 'طلب RCA' }).eq('id', incidentId);
      if (error) throw error;
      await supabase.from('incident_logs').insert({
        incident_id: incidentId,
        actor_id:    profile?.id,
        actor_name:  profile?.name ?? 'مشرف الجودة',
        action_type: 'request_rca',
        action_name: 'طلب تحليل جذري',
        description: 'تم طلب إجراء تحليل السبب الجذري (RCA) من قِبَل مشرف الجودة',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quality-all-incidents'] });
      qc.invalidateQueries({ queryKey: ['incident-logs', selectedIncident?.id] });
      push('تم إرسال طلب RCA بنجاح', 'success');
      setSelectedIncident(null);
    },
    onError: (e: any) => push(e.message, 'error'),
  });

  /* ─── Print helpers ─────────────────────────────────────────────────────── */
  const printIncidentList = (list: IncidentWithDept[], title: string) => {
    const rows = list.map(i => `
      <tr>
        <td dir="ltr" style="font-family:monospace">${i.event_id}</td>
        <td>${i.reporter_name}</td>
        <td>${i.location}</td>
        <td>${i.event_type}</td>
        <td>${i.risk_level}</td>
        <td>${i.status}</td>
        <td dir="ltr">${new Date(i.created_at).toLocaleDateString('en-GB')}</td>
      </tr>`).join('');
    const H = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>${title}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap');
        *{font-family:'Tajawal',sans-serif;box-sizing:border-box;}
        body{background:#fff;color:#0f172a;font-size:12px;padding:20px;}
        h1{font-size:18px;font-weight:900;text-align:center;margin-bottom:4px;}
        .sub{text-align:center;color:#64748b;font-size:11px;margin-bottom:20px;}
        table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;}
        th,td{padding:8px 10px;text-align:right;border:1px solid #e2e8f0;font-size:11px;}
        th{background:#0d9488;color:#fff;font-weight:900;}
        tr:nth-child(even){background:#f8fafc;}
        .footer{text-align:center;margin-top:16px;font-size:10px;color:#94a3b8;}
        @media print{@page{margin:1.5cm;size:A4 landscape;}}
      </style></head><body>
      <h1>نظام مُناوِب | ${title}</h1>
      <div class="sub">تاريخ الطباعة: ${new Date().toLocaleString('ar-SA')}</div>
      <table>
        <thead><tr><th>رقم الحدث</th><th>المُبلِّغ</th><th>القسم</th><th>النوع</th><th>الخطورة</th><th>الحالة</th><th>التاريخ</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">إجمالي السجلات: ${list.length} — نظام مُناوِب للإدارة التشغيلية</div>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(H); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500); }
  };

  const printEventReport = (inc: IncidentWithDept, logs: IncidentLogRow[]) => {
    const statusBadge = (s: string) => {
      const map: Record<string,string> = { 'مفتوح':'#2563eb','مصعد':'#ea580c','موجه':'#0284c7','طلب RCA':'#9333ea','مغلق':'#64748b','RCA Completed':'#16a34a' };
      return `<span style="background:${map[s]??'#64748b'};color:#fff;border-radius:8px;padding:3px 12px;font-size:11px;font-weight:900;">${s}</span>`;
    };
    const logRows = logs.map(l => `
      <tr>
        <td>${l.actor_name ?? 'النظام'}</td>
        <td>${l.action_name ?? l.action_type}</td>
        <td>${l.description ?? '—'}</td>
        <td dir="ltr" style="font-size:10px;">${new Date(l.created_at).toLocaleString('en-GB',{dateStyle:'short',timeStyle:'short'})}</td>
      </tr>`).join('');
    const H = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>تقرير حدث — ${inc.event_id}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap');
        *{font-family:'Tajawal',sans-serif;box-sizing:border-box;margin:0;padding:0;}
        body{background:#f1f5f9;padding:20px;font-size:12px;color:#0f172a;}
        .card{background:#fff;border-radius:20px;overflow:hidden;max-width:820px;margin:0 auto;box-shadow:0 4px 24px rgba(0,0,0,0.08);}
        .hdr{background:linear-gradient(135deg,#0f172a,#0d9488);padding:20px 28px;color:#fff;}
        .hdr h1{font-size:20px;font-weight:900;margin-bottom:4px;}
        .hdr p{font-size:11px;opacity:0.7;}
        .body{padding:24px 28px;}
        .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:16px;}
        .field{background:#f8fafc;border-radius:10px;padding:10px 14px;border:1px solid #e2e8f0;}
        .field .lbl{font-size:10px;color:#94a3b8;font-weight:700;margin-bottom:3px;}
        .field .val{font-size:13px;font-weight:900;}
        .desc{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:14px;margin-bottom:16px;}
        .desc .lbl{font-size:10px;color:#64748b;font-weight:700;margin-bottom:5px;}
        .desc p{font-size:13px;font-weight:700;line-height:1.7;}
        table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;}
        th,td{padding:8px 12px;text-align:right;border-bottom:1px solid #f1f5f9;font-size:11px;}
        th{background:#0d9488;color:#fff;font-weight:900;}
        .footer{text-align:center;padding:14px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;}
        *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
        @media print{body{background:#fff;}@page{margin:1.5cm;size:A4;}}
      </style></head><body><div class="card">
      <div class="hdr">
        <h1>تقرير الحدث التشغيلي — ${inc.event_id}</h1>
        <p>صادر عن نظام مُناوِب | تاريخ الطباعة: ${new Date().toLocaleString('ar-SA')}</p>
      </div>
      <div class="body">
        <div class="grid">
          <div class="field"><div class="lbl">رقم الحدث</div><div class="val" dir="ltr" style="font-family:monospace">${inc.event_id}</div></div>
          <div class="field"><div class="lbl">الحالة</div><div class="val">${statusBadge(inc.status)}</div></div>
          <div class="field"><div class="lbl">القسم / الموقع</div><div class="val">${inc.location}</div></div>
          <div class="field"><div class="lbl">مستوى الخطورة</div><div class="val">${inc.risk_level}</div></div>
          <div class="field"><div class="lbl">التصنيف</div><div class="val">${inc.event_type}</div></div>
          <div class="field"><div class="lbl">المُبلِّغ</div><div class="val">${inc.reporter_name}</div></div>
        </div>
        <div class="desc">
          <div class="lbl">الوصف الميداني</div>
          <p>${inc.description}</p>
        </div>
        ${logs.length > 0 ? `
        <div style="font-weight:900;font-size:13px;margin-bottom:8px;">📜 التسلسل الزمني للإجراءات</div>
        <table>
          <thead><tr><th>المنفذ</th><th>الإجراء</th><th>التفاصيل</th><th>التاريخ</th></tr></thead>
          <tbody>${logRows}</tbody>
        </table>` : ''}
      </div>
      <div class="footer">تقرير رسمي صادر من نظام مُناوِب للإدارة التشغيلية</div>
    </div></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(H); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500); }
  };

  const TABS: { id: TabId; label: string; badge?: number }[] = [
    { id: 'overview',   label: 'الرادار والمؤشرات' },
    { id: 'rca',        label: 'طلبات التحليل الجذري (RCA)', badge: rcaPending },
    { id: 'all-events', label: 'سجل الأحداث الشامل' },
  ];

  /* ════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 dark:from-slate-950 dark:to-teal-950" dir="rtl">

      {/* ── Toasts ── */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 items-center pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`px-5 py-3 rounded-2xl text-white text-sm font-black shadow-xl pointer-events-auto ${
            t.type === 'success' ? 'bg-teal-600' : t.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
          }`}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-10 h-10 rounded-xl" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <div>
              <h1 className="font-black text-lg text-slate-800 dark:text-white flex items-center gap-2">
                مُـناوِب
                <span className="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 text-[10px] px-2 py-0.5 rounded-full font-black border border-teal-200 dark:border-teal-800 uppercase">Quality</span>
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">إدارة الجودة والتحليل المستمر</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-300 hidden sm:block">{profile?.name}</span>
            {/* Notifications bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifs(v => !v)}
                className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center hover:scale-110 transition border border-blue-100 dark:border-blue-900 text-sm relative"
              >
                🔔
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] font-black flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute top-12 left-0 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                    <span className="font-black text-sm text-slate-800 dark:text-white">مركز التنبيهات</span>
                    {notifications.length > 0 && (
                      <button onClick={markAllRead} className="text-[10px] font-black text-blue-600 hover:text-blue-800">تعيين كمقروء</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                    {notifications.length === 0 ? (
                      <p className="text-center text-slate-400 text-xs py-6">لا توجد إشعارات جديدة</p>
                    ) : notifications.map((n: any) => (
                      <div key={n.id} className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-xs font-bold text-slate-700 dark:text-slate-300">
                        {n.message}
                        <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowProfile(true)}
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
              title="الملف الشخصي"
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
            </button>
            <button
              onClick={() => setDarkMode(d => !d)}
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-yellow-400 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={() => setShowLogout(true)} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto gap-1 pb-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 py-2.5 font-black text-sm whitespace-nowrap transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="absolute -top-1 -left-1 w-5 h-5 bg-purple-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ══ OVERVIEW ══════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800 dark:text-white">مؤشرات جودة الأداء (Quality KPIs)</h2>
              <button onClick={() => qc.invalidateQueries({ queryKey: ['quality-all-incidents'] })} className="btn-secondary text-xs px-3 py-1.5">تحديث البيانات</button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div
                className="glass-panel p-5 rounded-2xl border-r-4 border-blue-500 relative overflow-hidden group hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setAllEventsStatus(''); setAllEventsSearch(''); setActiveTab('all-events'); }}
              >
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">إجمالي الأحداث المسجلة</p>
                <p className="text-3xl font-black text-slate-800 dark:text-white">{allLoading ? '--' : totalIncidents}</p>
                <span className="absolute left-3 bottom-3 text-4xl opacity-5 select-none">📋</span>
              </div>
              <div
                className="glass-panel p-5 rounded-2xl border-r-4 border-purple-500 relative overflow-hidden group hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setRcaStatusFilter('طلب RCA'); setActiveTab('rca'); }}
              >
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">أحداث تتطلب RCA</p>
                <p className="text-3xl font-black text-purple-600">{allLoading ? '--' : rcaPending}</p>
                <span className="absolute left-3 bottom-3 text-4xl opacity-5 select-none">🔬</span>
              </div>
              <div
                className="glass-panel p-5 rounded-2xl border-r-4 border-orange-500 relative overflow-hidden group hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setActiveTab('all-events'); }}
              >
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">معدل التكرار</p>
                <p className="text-3xl font-black text-orange-600">{allLoading ? '--' : repeatedCount}</p>
                <span className="absolute left-3 bottom-3 text-4xl opacity-5 select-none">🔁</span>
              </div>
              <div
                className="glass-panel p-5 rounded-2xl border-r-4 border-red-500 relative overflow-hidden group hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setAllEventsSearch(''); setAllEventsStatus(''); setActiveTab('all-events'); }}
              >
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">أحداث حرجة وعالية الخطورة</p>
                <p className="text-3xl font-black text-red-600">{allLoading ? '--' : highRiskCount}</p>
                <span className="absolute left-3 bottom-3 text-4xl opacity-5 select-none">🛡️</span>
              </div>
            </div>

            {/* Charts */}
            <div>
              <h3 className="text-base font-black text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                📊 التحليل البياني للجودة
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

                {/* Status Chart */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">توزيع حالات الأحداث</p>
                  {allLoading ? <ChartSkeleton /> : statusEntries.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <div className="space-y-2">
                      {statusEntries.map(([status, count]) => {
                        const pct = totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0;
                        return (
                          <div key={status}>
                            <div className="flex justify-between text-xs font-bold mb-0.5">
                              <span className="text-slate-600 dark:text-slate-300 truncate max-w-[100px]">{status}</span>
                              <span className="text-slate-400">{count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: statusColors[status] ?? '#94a3b8' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Risk Chart */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">توزيع مستويات الخطورة</p>
                  {allLoading ? <ChartSkeleton /> : riskEntries.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <div className="space-y-2">
                      {riskEntries.map(([level, count]) => {
                        const pct = totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0;
                        const colorMap: Record<string, string> = { 'منخفض': '#10b981', 'متوسط': '#f59e0b', 'عالي': '#f97316', 'مرتفع': '#ef4444', 'حرج': '#7c3aed' };
                        return (
                          <div key={level}>
                            <div className="flex justify-between text-xs font-bold mb-0.5">
                              <span className="text-slate-600 dark:text-slate-300">{level}</span>
                              <span className="text-slate-400">{count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colorMap[level] ?? '#94a3b8' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Repeated Chart */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">الأحداث المتكررة</p>
                  {allLoading ? <ChartSkeleton /> : totalIncidents === 0 ? (
                    <EmptyChart />
                  ) : (
                    <div className="space-y-2">
                      {repeatedEntries.map(([label, count]) => {
                        const pct = totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0;
                        return (
                          <div key={label}>
                            <div className="flex justify-between text-xs font-bold mb-0.5">
                              <span className="text-slate-600 dark:text-slate-300">{label}</span>
                              <span className="text-slate-400">{count} ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: label === 'متكرر' ? '#f97316' : '#10b981' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Dept Chart */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">الأحداث حسب القسم</p>
                  {allLoading ? <ChartSkeleton /> : deptEntries.length === 0 ? (
                    <EmptyChart />
                  ) : (
                    <div className="space-y-2">
                      {deptEntries.map(([dept, count]) => {
                        const maxVal = deptEntries[0][1];
                        const pct = maxVal > 0 ? Math.round((count / maxVal) * 100) : 0;
                        return (
                          <div key={dept}>
                            <div className="flex justify-between text-xs font-bold mb-0.5">
                              <span className="text-slate-600 dark:text-slate-300 truncate max-w-[100px]">{dept}</span>
                              <span className="text-slate-400">{count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-teal-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ RCA TAB ════════════════════════════════════════════════════════ */}
        {activeTab === 'rca' && (
          <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <h2 className="text-xl font-black text-slate-800 dark:text-white">صندوق وارد الجودة (طلبات RCA)</h2>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="search"
                  className="input-field text-sm w-48"
                  placeholder="بحث في طلبات RCA..."
                  value={rcaSearch}
                  onChange={e => setRcaSearch(e.target.value)}
                />
                <select
                  className="input-field text-sm"
                  value={rcaStatusFilter}
                  onChange={e => setRcaStatusFilter(e.target.value)}
                >
                  <option value="">الكل (طلب + مكتمل)</option>
                  <option value="طلب RCA">طلب RCA — معلق</option>
                  <option value="RCA Completed">مكتمل التحليل</option>
                </select>
                <span className="text-xs font-bold text-slate-500">{filteredRcaIncidents.length} نتيجة</span>
                <button
                  onClick={() => printIncidentList(filteredRcaIncidents, 'طلبات التحليل الجذري RCA')}
                  className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                >
                  🖨️ طباعة القائمة
                </button>
              </div>
            </div>

            {allLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1,2,3].map(k => <div key={k} className="h-44 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />)}
              </div>
            ) : filteredRcaIncidents.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-4xl mb-3">🔬</p>
                <p className="text-slate-400 font-bold text-sm">لا توجد طلبات RCA</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredRcaIncidents.map(inc => {
                  const colors = RISK_LEVEL_COLORS[inc.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
                  const existingRca = rcaList.find(r => r.incident_id === inc.id);
                  return (
                    <div key={inc.id} className={`glass-panel rounded-2xl p-5 border ${colors.bg} hover:shadow-md transition-all`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-black text-slate-400">{inc.event_id}</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${colors.badge}`}>{inc.risk_level}</span>
                        {inc.status === 'RCA Completed' && (
                          <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">مكتمل</span>
                        )}
                      </div>
                      <p className="text-sm font-black text-slate-800 dark:text-white mb-1">{inc.event_type}</p>
                      <p className="text-xs text-slate-500 mb-0.5">{inc.location}</p>
                      <p className="text-xs text-slate-400 mb-3">{formatDate(inc.created_at)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">{truncate(inc.description, 80)}</p>
                      <button
                        onClick={() => {
                          if (existingRca) {
                            setSelectedRca(existingRca);
                          } else {
                            setShowRcaForm(inc.id);
                          }
                        }}
                        className={existingRca ? 'btn-secondary text-xs w-full' : 'btn-primary text-xs w-full'}
                      >
                        {existingRca ? 'تعديل التحليل' : 'بدء التحليل الجذري'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ ALL EVENTS ════════════════════════════════════════════════════ */}
        {activeTab === 'all-events' && (
          <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-3">
              <h2 className="text-xl font-black text-slate-800 dark:text-white">السجل الشامل للأحداث التشغيلية</h2>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="search"
                  className="input-field text-sm w-52"
                  placeholder="بحث في الأحداث..."
                  value={allEventsSearch}
                  onChange={e => setAllEventsSearch(e.target.value)}
                />
                <select
                  className="input-field text-sm"
                  value={allEventsStatus}
                  onChange={e => setAllEventsStatus(e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="مفتوح">مفتوح</option>
                  <option value="مصعد">مصعد</option>
                  <option value="طلب RCA">طلب RCA</option>
                  <option value="RCA Completed">اكتمل التحليل</option>
                  <option value="مغلق">مغلق</option>
                </select>
                <span className="text-xs font-bold text-slate-500">{filteredAllEvents.length} نتيجة</span>
                <button
                  onClick={() => printIncidentList(filteredAllEvents, 'السجل الشامل للأحداث التشغيلية')}
                  className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                >
                  🖨️ طباعة القائمة
                </button>
              </div>
            </div>

            {allLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1,2,3,4].map(k => <div key={k} className="h-44 bg-slate-200 dark:bg-slate-800 rounded-2xl animate-pulse" />)}
              </div>
            ) : filteredAllEvents.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-4xl mb-3">📭</p>
                <p className="text-slate-400 font-bold text-sm">لا توجد نتائج مطابقة</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredAllEvents.map(inc => {
                  const colors = RISK_LEVEL_COLORS[inc.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
                  return (
                    <button
                      key={inc.id}
                      onClick={() => setSelectedIncident(inc)}
                      className={`glass-panel rounded-2xl p-5 text-right border ${colors.bg} hover:shadow-md transition-all hover:-translate-y-0.5`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-black text-slate-400">{inc.event_id}</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${colors.badge}`}>{inc.risk_level}</span>
                        {inc.is_repeated && <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">متكرر</span>}
                      </div>
                      <p className="text-sm font-black text-slate-800 dark:text-white mb-1">{inc.event_type}</p>
                      <p className="text-xs text-slate-500 mb-0.5">{inc.location}</p>
                      <p className="text-xs text-slate-400 mb-2">{formatDate(inc.created_at)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">{truncate(inc.description, 80)}</p>
                      <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${
                        inc.status === 'مغلق' || inc.status === 'RCA Completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        inc.status === 'مصعد' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        inc.status === 'طلب RCA' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {inc.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </main>

      {/* ══ INCIDENT DETAIL MODAL (Quality Override) ════════════════════════ */}
      {selectedIncident && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedIncident(null)}>
          <div
            className="glass-panel w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] border border-teal-500/30 dark:border-teal-500/20"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-l from-teal-800 to-slate-900 p-5 text-white flex justify-between items-center shrink-0 gap-3 flex-wrap">
              <h3 className="font-black text-lg flex items-center gap-2">
                🔬 تدقيق وتقييم الجودة
                <span className="text-xs bg-white/10 border border-white/20 px-3 py-1 rounded-lg font-mono">{selectedIncident.event_id}</span>
              </h3>
              <div className="flex items-center gap-2">
                {/* Request RCA button — only when not already in RCA flow */}
                {!['طلب RCA','RCA Completed'].includes(selectedIncident.status) && (
                  <button
                    onClick={() => requestRca(selectedIncident.id)}
                    disabled={requestingRca}
                    className="text-white/80 hover:text-white bg-purple-500/30 hover:bg-purple-500/60 border border-purple-400/40 px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5"
                  >
                    🔬 {requestingRca ? '⏳...' : 'طلب RCA'}
                  </button>
                )}
                {/* WhatsApp share */}
                <button
                  onClick={() => {
                    const msg = [
                      `📋 *حدث تشغيلي — ${selectedIncident.event_id}*`,
                      '━━━━━━━━━━━━━━━━━',
                      `📍 *الموقع:* ${selectedIncident.location}`,
                      `⚡ *النوع:* ${selectedIncident.event_type}`,
                      `⚠️ *الخطورة:* ${selectedIncident.risk_level}`,
                      `📊 *الحالة:* ${selectedIncident.status}`,
                      `📝 *الوصف:*\n${selectedIncident.description}`,
                      '━━━━━━━━━━━━━━━━━',
                      '🏥 نظام مُناوِب',
                    ].join('\n');
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  title="مشاركة عبر واتساب"
                  className="text-white/80 hover:text-white bg-green-500/20 hover:bg-green-500/40 border border-green-400/30 px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 32 32" fill="#25d366"><path d="M16 3C8.82 3 3 8.82 3 16c0 2.62.76 5.07 2.08 7.14L3 29l6.05-2.04A13 13 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm6.37 17.84c-.27.76-1.57 1.44-2.15 1.53-.56.09-1.27.13-2.05-.13-.47-.15-1.07-.36-1.84-.7-3.23-1.39-5.34-4.68-5.5-4.9-.16-.22-1.3-1.73-1.3-3.3 0-1.57.82-2.35 1.12-2.67.3-.32.65-.4.86-.4h.62c.2 0 .47-.07.73.56.27.65.92 2.24.99 2.4.08.16.13.36.03.57-.1.22-.16.35-.32.54l-.47.55c-.16.16-.33.33-.14.65.18.32.82 1.36 1.77 2.2 1.22 1.08 2.25 1.41 2.57 1.57.32.16.51.13.7-.08.18-.22.77-.9 1-.21.22.7.22 1.32-.05 1.62z"/></svg>
                  واتساب
                </button>
                {/* Print report */}
                <button
                  onClick={() => printEventReport(selectedIncident, incidentLogs)}
                  title="طباعة تقرير الحدث"
                  className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1.5"
                >
                  🖨️ طباعة
                </button>
                <button onClick={() => setSelectedIncident(null)} className="text-white/60 hover:text-white text-xl transition">✕</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/50 dark:bg-slate-900/50">

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'القسم والموقع', value: selectedIncident.location },
                  { label: 'التصنيف', value: selectedIncident.event_type },
                  { label: 'مستوى الخطورة', value: selectedIncident.risk_level },
                  { label: 'الحالة', value: selectedIncident.status },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700">
                    <p className="text-[10px] font-bold text-slate-400 mb-1">{label}</p>
                    <p className="font-black text-sm text-slate-800 dark:text-white">{value}</p>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">الوصف الميداني الكامل</p>
                <p className="font-bold text-sm text-slate-800 dark:text-slate-200 leading-loose">{selectedIncident.description}</p>
                {selectedIncident.is_repeated && (
                  <span className="mt-2 inline-block bg-orange-100 text-orange-700 border border-orange-200 px-3 py-1 rounded-lg text-xs font-black">حدث متكرر</span>
                )}
              </div>

              {/* Quality Override Panel */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-slate-800 dark:to-teal-900/20 p-5 rounded-2xl border border-teal-200 dark:border-teal-800">
                <h4 className="font-black text-base mb-4 text-teal-900 dark:text-teal-300 flex items-center gap-2">
                  ⚙️ صلاحية الجودة: تصحيح وتقييم الحدث
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-bold mb-1 text-slate-700 dark:text-slate-300">التصنيف الصحيح</label>
                    <select
                      className="input-field text-sm"
                      value={overrideForm.type}
                      onChange={e => setOverrideForm(p => ({ ...p, type: e.target.value }))}
                    >
                      <option value={selectedIncident.event_type}>{selectedIncident.event_type} (الحالي)</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.sub_cat ?? c.main_cat}>
                          {c.main_cat}{c.sub_cat ? ` / ${c.sub_cat}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1 text-slate-700 dark:text-slate-300">مستوى الخطورة الصحيح (Risk Override)</label>
                    <select
                      className="input-field text-sm"
                      value={overrideForm.risk}
                      onChange={e => setOverrideForm(p => ({ ...p, risk: e.target.value }))}
                    >
                      {['منخفض', 'متوسط', 'عالي', 'مرتفع', 'حرج'].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-bold mb-1 text-slate-700 dark:text-slate-300">
                    مبرر التدخل وتعديل الجودة <span className="text-red-500 font-black">*</span>
                  </label>
                  <textarea
                    rows={2}
                    className="input-field text-sm"
                    placeholder="اكتب سبب قيام إدارة الجودة بتغيير تصنيف أو خطورة الحدث..."
                    value={overrideForm.justification}
                    onChange={e => setOverrideForm(p => ({ ...p, justification: e.target.value }))}
                  />
                </div>
                <button
                  onClick={submitQualityOverride}
                  disabled={overrideSaving}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-black py-3 rounded-xl transition-all shadow-lg flex justify-center items-center gap-2"
                >
                  {overrideSaving ? <span className="spinner border-white/30 border-t-white" /> : null}
                  ✅ اعتماد تقييم الجودة وتوثيقه
                </button>
              </div>

              {/* Logs */}
              <div>
                <h4 className="font-black text-sm text-slate-700 dark:text-white mb-3 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                  📜 التسلسل الزمني وسجلات النظام
                </h4>
                {incidentLogs.length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-4">لا توجد سجلات</p>
                ) : (
                  <div className="space-y-2">
                    {incidentLogs.map(log => (
                      <div key={log.id} className="flex gap-3 text-xs">
                        <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5 shrink-0" />
                        <div className="flex-1 pb-2 border-b border-slate-100 dark:border-slate-800">
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-black text-slate-700 dark:text-slate-300">{log.actor_name}</span>
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-400">{formatDate(log.created_at)}</span>
                            </div>
                            {log.description && (
                              <button
                                onClick={() => {
                                  const msg = [
                                    `🔄 *تحديث إجراء — ${selectedIncident.event_id}*`,
                                    '━━━━━━━━━━━━━━━━━',
                                    `📝 ${log.description}`,
                                    `👤 بواسطة: ${log.actor_name ?? 'النظام'}`,
                                    `🕒 ${new Date(log.created_at).toLocaleTimeString('ar-SA',{hour:'2-digit',minute:'2-digit'})}`,
                                    '━━━━━━━━━━━━━━━━━',
                                    '🏥 نظام مُناوِب',
                                  ].join('\n');
                                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                                }}
                                title="مشاركة عبر واتساب"
                                style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center' }}
                              >
                                <svg width="14" height="14" viewBox="0 0 32 32" fill="#25d366"><path d="M16 3C8.82 3 3 8.82 3 16c0 2.62.76 5.07 2.08 7.14L3 29l6.05-2.04A13 13 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm6.37 17.84c-.27.76-1.57 1.44-2.15 1.53-.56.09-1.27.13-2.05-.13-.47-.15-1.07-.36-1.84-.7-3.23-1.39-5.34-4.68-5.5-4.9-.16-.22-1.3-1.73-1.3-3.3 0-1.57.82-2.35 1.12-2.67.3-.32.65-.4.86-.4h.62c.2 0 .47-.07.73.56.27.65.92 2.24.99 2.4.08.16.13.36.03.57-.1.22-.16.35-.32.54l-.47.55c-.16.16-.33.33-.14.65.18.32.82 1.36 1.77 2.2 1.22 1.08 2.25 1.41 2.57 1.57.32.16.51.13.7-.08.18-.22.77-.9 1-.21.22.7.22 1.32-.05 1.62z"/></svg>
                              </button>
                            )}
                          </div>
                          <p className="font-bold text-slate-600 dark:text-slate-400">{log.action_name ?? log.action_type}</p>
                          {log.description && <p className="text-slate-500 dark:text-slate-500 mt-0.5">{log.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ RCA EDIT MODAL ═══════════════════════════════════════════════════ */}
      {(showRcaForm || selectedRca) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-panel w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="font-black text-base text-slate-800 dark:text-white">
                {selectedRca ? selectedRca.rca_id : 'تحليل السبب الجذري (5 لماذا)'}
              </h3>
              <button onClick={() => { setShowRcaForm(null); setSelectedRca(null); }} className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
                <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <RcaForm
                incidentId={showRcaForm ?? selectedRca!.incident_id}
                rca={selectedRca ?? undefined}
                onSave={saveRca}
                saving={rcaSaving}
                onCancel={() => { setShowRcaForm(null); setSelectedRca(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ══ PROFILE MODAL ════════════════════════════════════════════════════ */}
      {showProfile && (
        <QualityProfileModal
          profile={profile}
          darkMode={darkMode}
          onClose={() => setShowProfile(false)}
          onSuccess={(msg) => { push(msg, 'success'); setShowProfile(false); }}
          onError={(msg) => push(msg, 'error')}
        />
      )}

      {/* ══ LOGOUT MODAL ═════════════════════════════════════════════════════ */}
      {showLogout && (
        <QualityLogoutModal
          onConfirm={handleSignOut}
          onCancel={() => setShowLogout(false)}
        />
      )}

    </div>
  );
}

/* ─── RcaForm ─────────────────────────────────────────────────────────────── */

interface RcaFormProps {
  incidentId: string;
  rca?:       Partial<QualityRcaRow>;
  onSave:     (payload: Partial<QualityRcaRow> & { incident_id: string }) => Promise<void>;
  saving:     boolean;
  onCancel:   () => void;
}

function RcaForm({ incidentId, rca, onSave, saving, onCancel }: RcaFormProps) {
  const [form, setForm] = useState({
    why_1:       rca?.why_1       ?? '',
    why_2:       rca?.why_2       ?? '',
    why_3:       rca?.why_3       ?? '',
    why_4:       rca?.why_4       ?? '',
    root_cause:  rca?.root_cause  ?? '',
    action_plan: rca?.action_plan ?? '',
    owner:       rca?.owner       ?? '',
    deadline:    rca?.deadline    ?? '',
    status:      rca?.status      ?? 'قيد التحليل',
  });

  const handleSave = () => onSave({ incident_id: incidentId, id: rca?.id, ...form });

  return (
    <div className="space-y-4">
      {(['why_1', 'why_2', 'why_3', 'why_4'] as const).map((field, idx) => (
        <div key={field}>
          <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">لماذا #{idx + 1}</label>
          <input type="text" className="input-field" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
        </div>
      ))}
      <div>
        <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">السبب الجذري</label>
        <textarea rows={3} className="input-field resize-none" value={form.root_cause} onChange={e => setForm(p => ({ ...p, root_cause: e.target.value }))} />
      </div>
      <div>
        <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">خطة الإجراء</label>
        <textarea rows={3} className="input-field resize-none" value={form.action_plan} onChange={e => setForm(p => ({ ...p, action_plan: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">المسؤول</label>
          <input type="text" className="input-field" value={form.owner} onChange={e => setForm(p => ({ ...p, owner: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">الموعد النهائي</label>
          <input type="date" className="input-field" dir="ltr" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">الحالة</label>
        <select className="input-field" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
          <option value="قيد التحليل">قيد التحليل</option>
          <option value="مكتمل">مكتمل</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="btn-secondary flex-1">إلغاء</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
          {saving ? <span className="spinner border-white/30 border-t-white" /> : 'حفظ التحليل'}
        </button>
      </div>
    </div>
  );
}

/* ─── Quality Profile Modal ─────────────────────────────────────────────── */

function QualityProfileModal({ profile, darkMode, onClose, onSuccess, onError }: {
  profile: any; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const [phone, setPhone]         = useState<string>((profile as any)?.phone ?? '');
  const [oldPass, setOldPass]     = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confPass, setConfPass]   = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifWa, setNotifWa]       = useState(true);
  const passError = newPass && confPass && newPass !== confPass;

  const handleSave = async () => {
    if (newPass && !oldPass) { onError('يجب إدخال كلمة المرور الحالية أولاً.'); return; }
    if (passError) { onError('كلمتا المرور الجديدتان غير متطابقتين!'); return; }
    setSaving(true);
    try {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ phone, notif_email: notifEmail, notif_wa: notifWa } as any)
        .eq('id', profile?.id);
      if (profileErr) throw profileErr;
      if (newPass) {
        const { error: passErr } = await supabase.auth.updateUser({ password: newPass });
        if (passErr) throw passErr;
      }
      onSuccess('تم حفظ تحديثات الملف الشخصي بنجاح ✅');
    } catch (e: any) {
      onError(e?.message ?? 'حدث خطأ أثناء الحفظ');
    } finally { setSaving(false); }
  };

  const inputCls = `w-full px-3 py-2.5 rounded-xl border text-sm font-bold outline-none focus:ring-2 focus:ring-teal-500 ${darkMode ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-800'}`;
  const disabledCls = `${inputCls} opacity-60 cursor-not-allowed`;
  const lbl = (t: string) => <div className="text-xs font-bold text-slate-500 mb-1.5">{t}</div>;

  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200'}`}>
        {/* Header */}
        <div className="bg-gradient-to-l from-teal-800 to-slate-900 p-4 flex justify-between items-center">
          <div className="text-white font-black text-base">⚙️ إعدادات الملف الشخصي</div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-xl transition">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* User info */}
          <div className={`rounded-xl p-4 border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <div className="text-teal-600 text-xs font-black mb-3 uppercase tracking-wider">👤 بيانات المستخدم</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>{lbl('الاسم الكامل')}<input readOnly value={profile?.name ?? ''} className={disabledCls} /></div>
              <div>{lbl('الإدارة / القسم')}<input readOnly value={(profile as any)?.department?.name ?? ''} className={disabledCls} /></div>
            </div>
            <div className="mb-3">{lbl('البريد الإلكتروني')}<input readOnly value={profile?.email ?? ''} dir="ltr" className={disabledCls} /></div>
            <div>
              {lbl('رقم الجوال')}
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm font-bold">+966</span>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="5xxxxxxxx" dir="ltr" className={`${inputCls} flex-1`} />
              </div>
            </div>
          </div>

          {/* Password */}
          <div className={`rounded-xl p-4 border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <div className="text-teal-600 text-xs font-black mb-3 uppercase tracking-wider">🔒 إعدادات الأمان</div>
            <p className="text-slate-400 text-xs font-bold mb-3">اترك حقول كلمة المرور فارغة إذا لم ترغب في تغييرها.</p>
            <div className="space-y-3">
              <div>
                {lbl('كلمة المرور الحالية')}
                <div className="relative">
                  <input type={showOld ? 'text' : 'password'} value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="••••••••" className={`${inputCls} pl-9`} />
                  <button onClick={() => setShowOld(v => !v)} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{showOld ? '🙈' : '👁️'}</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('كلمة المرور الجديدة')}
                  <div className="relative">
                    <input type={showNew ? 'text' : 'password'} value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="••••••••" className={`${inputCls} pl-9`} />
                    <button onClick={() => setShowNew(v => !v)} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{showNew ? '🙈' : '👁️'}</button>
                  </div>
                </div>
                <div>
                  {lbl('تأكيد كلمة المرور')}
                  <input type="password" value={confPass} onChange={e => setConfPass(e.target.value)} placeholder="••••••••" className={`${inputCls} ${passError ? 'border-red-500 ring-1 ring-red-500' : ''}`} />
                </div>
              </div>
              {passError && <p className="text-red-500 text-xs font-bold">⚠️ كلمتا المرور الجديدتان غير متطابقتين!</p>}
            </div>
          </div>

          {/* Notification prefs */}
          <div className={`rounded-xl p-4 border ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <div className="text-teal-600 text-xs font-black mb-3 uppercase tracking-wider">🔔 تفضيلات الإشعارات</div>
            {[
              { label:'إشعارات البريد الإلكتروني', sub:'تحديثات الأحداث على البريد', val:notifEmail, set:setNotifEmail },
              { label:'إشعارات واتساب (WhatsApp)',  sub:'التنبيهات العاجلة والطوارئ',  val:notifWa,    set:setNotifWa },
            ].map(item => (
              <div key={item.label} className="flex justify-between items-center pb-3 mb-3 border-b border-slate-200 dark:border-slate-700 last:border-0 last:pb-0 last:mb-0">
                <div>
                  <div className="font-bold text-sm">{item.label}</div>
                  <div className="text-slate-400 text-xs font-bold mt-0.5">{item.sub}</div>
                </div>
                <button
                  onClick={() => item.set((v: boolean) => !v)}
                  className={`w-11 h-6 rounded-full relative transition-colors ${item.val ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${item.val ? 'left-5' : 'left-0.5'}`} />
                </button>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1 py-2.5">إلغاء</button>
            <button onClick={handleSave} disabled={saving || !!passError} className={`flex-[2] py-2.5 rounded-xl font-black text-sm text-white transition ${saving || passError ? 'bg-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'}`}>
              {saving ? '⏳ جارٍ الحفظ...' : '💾 حفظ التحديثات'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Quality Logout Modal ───────────────────────────────────────────────── */

function QualityLogoutModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div onClick={onCancel} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-slate-800 rounded-2xl p-7 w-full max-w-sm text-center shadow-2xl border border-slate-200 dark:border-slate-700" style={{ animation:'modalIn 0.2s ease' }}>
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">🔴</div>
        <div className="font-black text-lg mb-2 text-slate-800 dark:text-white">تسجيل الخروج</div>
        <p className="text-slate-400 text-sm font-bold mb-6 leading-relaxed">هل أنت متأكد من رغبتك في الخروج من نظام مُناوِب؟</p>
        <div className="flex gap-3 justify-center">
          <button onClick={onCancel} className="btn-secondary px-6 py-2.5">إلغاء</button>
          <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-black px-6 py-2.5 rounded-xl transition shadow-md">🚪 نعم، خروج</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Small helpers ─────────────────────────────────────────────────────── */

function ChartSkeleton() {
  return (
    <div className="space-y-2.5 py-2">
      {[80, 60, 45, 30].map(w => (
        <div key={w} className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

function EmptyChart() {
  return <p className="text-center text-slate-400 text-xs py-8">لا توجد بيانات</p>;
}
